import Image from "next/image";
import clsx from "clsx";

import {
  BASE_64_1X1_GREY,
  language,
  STANDALONE_BASKET,
  VIEW,
} from "@chef/constants";
import {
  openModal,
  OrderQuery,
  useOrderQuery,
  useProductCategoriesQuery,
  useProductsByCategoriesQuery,
} from "@chef/state-management";
import {
  getPriceOfProductByVariation,
  getDiscountedAmount,
  getProductFeaturedImage,
  getStandaloneProductCategoryIdsFromProductCategories,
  getVariationFromProductsByCategories,
  getBasketProductQuantity,
} from "@chef/state-management/helpers";
import {
  useAppDispatch,
  useStandaloneBasket,
} from "@chef/state-management/hooks";
import { Card, CurrencyWithDiscount, Skeleton } from "@chef/components";
import { isEqualStrings } from "@chef/utils/equal";

import type { StandaloneBasketVariant } from "@chef/types/basket";
import type { View } from "@chef/types/common";

import { Controls } from "./Controls";
import { SubscriptionFooter } from "./SubscriptionFooter";

import { intl } from "./StandaloneProductTile.Intl";

type StandaloneProductTileProps = {
  isPrioritisedProduct?: boolean;
  variationId: string;
  week: number;
  year: number;
} & (
  | {
      view: Extract<View, "historic">;
      orderId: OrderQuery["order"]["orderId"];
    }
  | {
      view: Extract<View, "deviation">;
      productId: string;
      basketType: StandaloneBasketVariant;
    }
  | {
      view: Extract<View, "conversion">;
      productId: string;
    }
);

export const StandaloneProductTile = (props: StandaloneProductTileProps) => {
  const { week, year, variationId, isPrioritisedProduct, view } = props;

  const dispatch = useAppDispatch();
  const isHistoricView = view === VIEW.HISTORIC;
  const isDeviationView = view === VIEW.DEVIATION;
  const isDeviationBasketView =
    isDeviationView && props.basketType === STANDALONE_BASKET.DEVIATION;

  const { basket } = useStandaloneBasket({
    variant: isDeviationBasketView ? props.basketType : undefined,
    week,
    year,
  });

  const { orderItem, isLoading: isOrderLoading } = useOrderQuery(
    {
      orderId: isHistoricView ? props.orderId : undefined,
    },
    {
      skip: !isHistoricView,
      selectFromResult: ({ data, isLoading }) => ({
        orderItem: data?.order?.orderLines.find((ol) =>
          isEqualStrings(ol.variationId, variationId),
        ),
        isLoading,
      }),
    },
  );

  const { data: productCategoriesData, isLoading: isLoadingProductCategories } =
    useProductCategoriesQuery(undefined, {
      skip: isHistoricView,
    });

  const {
    productVariation,
    isLoading: isLoadingStandaloneProductsByCategories,
  } = useProductsByCategoriesQuery(
    {
      categoryIds: getStandaloneProductCategoryIdsFromProductCategories(
        productCategoriesData?.productCategories || [],
      ),
      week,
      year,
    },
    {
      skip: !productCategoriesData || isHistoricView,
      selectFromResult: ({ data, isLoading }) => ({
        productVariation: getVariationFromProductsByCategories({
          variationId,
          productsByCategories: data?.productsByCategories ?? [],
        }),
        isLoading,
      }),
    },
  );

  const variation = isHistoricView ? orderItem?.variation : productVariation;

  const isDataLoading =
    isOrderLoading ||
    isLoadingProductCategories ||
    isLoadingStandaloneProductsByCategories;

  if (!variation || isDataLoading) {
    return <Skeleton width={30} height={45} />;
  }

  const productId = variation.product.productId;
  const id = `${productId}:${variationId}`;
  const name = variation.name;
  const imageUrl = getProductFeaturedImage(variation);

  const quantity = isHistoricView && orderItem ? orderItem.quantity : 1;
  const displayName = isHistoricView ? `${quantity} x ${name}` : name;

  const discount = isHistoricView ? 0 : getDiscountedAmount(productVariation!);

  const price = isHistoricView
    ? orderItem?.fullPrice ?? 0
    : getPriceOfProductByVariation(productVariation!);

  const subscriptionFooterIsOpen = !!getBasketProductQuantity(
    basket,
    productId,
    variationId,
  );

  const onTriggerProductModal = () => {
    dispatch(
      openModal({
        id: "STANDALONE_PRODUCT",
        productId,
        variationId,
        week,
        year,
        view,
        basketType: isDeviationView ? props.basketType : undefined,
      }),
    );
  };

  return (
    <div className="relative flex flex-col shrink-0 grow">
      <Card
        className={clsx(
          "relative",
          subscriptionFooterIsOpen && "rounded-b-none",
        )}
        noPadding
      >
        <div className="relative flex flex-col h-full">
          <button
            type="button"
            onClick={onTriggerProductModal}
            className="relative p-2 h-36 group"
          >
            {!!discount && (
              <div className="absolute left-0 z-10 top-2">
                <p className="p-1 text-xs leading-none rounded-r-2xl -left-2 bg-highlight">
                  {intl.CAMPAIGN}
                </p>
              </div>
            )}
            <div className="relative w-full h-full overflow-hidden rounded-md">
              <Image
                alt={name}
                src={imageUrl || BASE_64_1X1_GREY}
                fill
                className="object-cover w-full h-full transition-transform duration-300 ease-in-out group-hover:scale-110"
                priority={isPrioritisedProduct}
                sizes="(min-width: 768px) 20vw, 25vw"
              />
            </div>
          </button>

          <div
            className="flex flex-col justify-between w-full p-2 pt-0 h-28 grow"
            id={id}
          >
            <button
              type="button"
              onClick={onTriggerProductModal}
              className="pt-1 text-base text-left line-clamp-2"
            >
              {displayName}
            </button>

            <div className="relative flex items-center min-w-full h-9">
              <div>
                <CurrencyWithDiscount
                  language={language}
                  discount={discount}
                  total={price}
                  emphasized
                  trimDecimal
                />
              </div>

              {isDeviationView && (
                <div className="absolute right-0 top-[50%] -translate-y-1/2">
                  <Controls
                    variation={productVariation!}
                    basketType={props.basketType}
                    week={props.week}
                    year={props.year}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>
      {isDeviationBasketView && (
        <SubscriptionFooter
          variation={productVariation!}
          basketType={props.basketType}
          week={props.week}
          year={props.year}
        />
      )}
    </div>
  );
};
